import { Calendar, CalendarDate } from "@/components/Calendar";
import { Flex, Popover, Text, TextInput } from "@mantine/core";
import { CalendarLevel } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useState } from "react";

// dayjs에 isoWeek 플러그인을 추가하여 주의 시작일을 월요일로 설정
dayjs.extend(isoWeek);

interface PopoverDatePickerProps {
  date: {
    selectedDate: CalendarDate;
    setSelectedDate: (date: CalendarDate) => void;
  };
  type?: "range" | "default"; // range 혹은 default 타입 지원
  defaultLevel?: CalendarLevel | "week";
  maxDate?: boolean;
}

// defaultLevel에 따른 날짜 포맷 정의
const getDateFormat = (level: CalendarLevel | "week") => {
  switch (level) {
    case "year":
      return "YYYY-MM";
    case "decade":
      return "YYYY";
    case "week":
      return "YYYY-MM-DD ~ YYYY-MM-DD";

    default: // 'month' 또는 기타 경우는 기본적으로 'YYYY-MM-DD'
      return "YYYY-MM-DD";
  }
};

export const PopoverDatePicker = ({
  date,
  type = "default",
  defaultLevel = "month",
  maxDate = false,
}: PopoverDatePickerProps) => {
  const { selectedDate, setSelectedDate } = date;
  const [opened, setOpened] = useState(false);
  const dateFormat = getDateFormat(defaultLevel);

  const handleDateChange = (date: CalendarDate) => {
    if (defaultLevel === "week" && date[0]) {
      // 주 단위 선택: 클릭한 날짜의 주 시작~끝 계산
      const startOfWeek = dayjs(date[0]).startOf("week").toDate();
      const endOfWeek = dayjs(date[0]).endOf("week").toDate();
      setSelectedDate([startOfWeek, endOfWeek]);
      setOpened(false);
    } else {
      setSelectedDate(date);
      if (type === "range" && date[0] && date[1]) {
        setOpened(false);
      } else if (type === "default" && date[0]) {
        setOpened(false);
      }
    }
  };

  // 캘린더 왼쪽 오른쪽 화살표 검은색으로 변경
  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      position="bottom"
      withArrow
      trapFocus
      closeOnEscape
    >
      <Popover.Target>
        <TextInput
          w={type === "range" ? "12rem" : "8rem"}
          label={
            <Flex align="center" gap="xs">
              <IconCalendar color="white" />
              <Text fz="md" fw={700} color="white">
                날짜
              </Text>
            </Flex>
          }
          value={
            defaultLevel === "week" && selectedDate[0] && selectedDate[1]
              ? `${dayjs(selectedDate[0]).format("YYYY-MM-DD")} ~ ${dayjs(selectedDate[1]).format(
                  "YYYY-MM-DD",
                )}`
              : type === "range"
              ? selectedDate[0] && selectedDate[1]
                ? `${dayjs(selectedDate[0]).format(dateFormat)} ~ ${dayjs(selectedDate[1]).format(
                    dateFormat,
                  )}`
                : ""
              : selectedDate[0]
              ? dayjs(selectedDate[0]).format(dateFormat)
              : ""
          }
          onClick={() => setOpened((o) => !o)}
          readOnly
          placeholder="날짜"
          styles={{ input: { cursor: "pointer" } }}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <Calendar
          dates={selectedDate}
          onChange={handleDateChange}
          size="md"
          type={type}
          defaultLevel={defaultLevel === "week" ? "month" : defaultLevel}
          maxDate={maxDate}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
